import { useMemo } from 'react';
import { PriceCard } from '../../../assets';
import { StarIcon } from '../../../assets/icons/startIcon';
import { formatNumber } from '../../../utils/formatNumber';
import { useGetProductsByStoreQuery } from '../../../redux/services/productApi';
import { useGetReviewsByStoreQuery } from '../../../redux/services/storesApi';
import styles from './style.module.css';
import Typography from '../../../components/Typography/Typography';

export interface IStoreDetails {
  storePreview: string;
  storeName: string;
  storeRating?: string;
  storeItemCount?: number;
  storePrice?: number;
  storeLocation: string;
  storeCreationDate: string;
  storeId: string;
  onClick?: () => void;
}

export const StoreDetailsCard: React.FC<IStoreDetails> = ({
  storePreview,
  storeName,
  storeLocation,
  storeCreationDate,
  storeId,
  onClick
}) => {
  const { data: products, isLoading: productsLoading } = useGetProductsByStoreQuery(storeId);
  
  const { data: reviews, isLoading: reviewsLoading } = useGetReviewsByStoreQuery(storeId);

  const storeItemCount = useMemo(() => {
    return products?.length || 0;
  }, [products]);

  const startingPrice = useMemo(() => {
    if (!products || products.length === 0) return 0;
    
    const prices = products.map(product => product.price);
    return Math.min(...prices);
  }, [products]);

  const { averageRating, totalReviews } = useMemo(() => {
    if (!reviews || reviews.length === 0) {
      return { averageRating: 0, totalReviews: 0 };
    }
    
    const totalRating = reviews.reduce((sum, review) => sum + review.rating, 0);
    const avgRating = totalRating / reviews.length;
    
    return {
      averageRating: avgRating.toFixed(1),
      totalReviews: reviews.length
    };
  }, [reviews]);

  if (productsLoading || reviewsLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.storeImage}>
          <img className={styles.image} src={storePreview} alt="Store Preview" />
        </div>
        <div className={styles.topDetails}>
          <div className={styles.storeName}>{storeName}</div>
          <div className={styles.ratings}>
            <p>Loading...</p>
          </div>
        </div>
        <div className={styles.midDetails}>
          <div className={styles.itemCount}>Loading...</div>
        </div>
        <div className={styles.bottomDetails}>
          <div className={styles.greyCont}>{storeLocation}</div>
          <div className={styles.greyCont}>{storeCreationDate}</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container} onClick={onClick}>
      <div className={styles.storeImage}>
        <img className={styles.image} src={storePreview} alt="Store Preview" />
      </div>

      <div className={styles.topDetails}>
        <div className={styles.storeName}>
          <Typography
            variant="bodyRegular"
            fontFamily='satoshi'
            className={styles.storeNameText}
          >
            {storeName}
            </Typography>
            </div>
        <div className={styles.ratings}>
          <Typography
            variant="bodyRegular"
            fontFamily='satoshi'
            className={styles.ratingValue}
          >{averageRating}
          </Typography>
          <StarIcon />
          <Typography
            variant="bodyRegular"
            fontFamily='satoshi'
            className={styles.ratingValue}
          >
            ({totalReviews} {totalReviews === 1 ? 'review' : 'reviews'})
            </Typography>
        </div>
      </div>

      <div className={styles.midDetails}>
        <div className={styles.itemCount}>
          <Typography
            variant="bodyRegular"
            fontFamily='satoshi'
            className={styles.itemCountText}
          >
          {storeItemCount} {storeItemCount === 1 ? 'Item' : 'Items'}
          </Typography>
        </div>
        <div className={styles.price}>
          <Typography
            variant="bodyRegular"
            fontFamily='satoshi'
            className={styles.priceText}
          >
          Starting Price: <PriceCard /> ₦{formatNumber(startingPrice)}
          </Typography>
        </div>
      </div>

      <div className={styles.bottomDetails}>
        <div className={styles.greyCont}>
          {storeLocation}
          </div>
        <div className={styles.greyCont}>{storeCreationDate}</div>
      </div>
    </div>
  );
};